/**
 * Route guard for the Settings → Accounts tab.
 *
 * The tab is filtered out of the strip when `isRsAccounts` is false (`SettingsShell`), but a tab
 * filter is not a gate: the ROUTE stayed reachable by URL, and every `settings.rudderstackAccounts.*`
 * procedure sits behind `requireAudiencesFlag` → `requireRsAccountsFlag` and answers NOT_FOUND. So a
 * workspace without account management that followed a link landed on a page with a header, an
 * enabled "Add account" button and "Could not load accounts." — on a tab it is not supposed to have.
 * So the gate lives on the route, not only on the tab strip.
 *
 * Mirrors `RequireUsage`, which wraps the Usage route for the same reason its tab is filtered.
 */
import { Navigate } from 'react-router-dom';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

interface RequireRsAccountsProps {
  children: React.ReactNode;
}

export function RequireRsAccounts({ children }: RequireRsAccountsProps): React.ReactElement {
  // `isRsAccounts` is `isAudiences && rsAccountsEnabled` — the same conjunction the server's gate
  // chain applies, so the two halves (an audiences-disabled workspace, a challenge workspace) are
  // both covered by the one flag.
  const { isRsAccounts } = useWorkspace();

  if (!isRsAccounts) {
    return <Navigate to=".." replace />;
  }

  return <>{children}</>;
}
